import React, { useRef, useMemo, Suspense, useState, useEffect } from 'react';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import { Sphere, OrbitControls, Html, Line } from '@react-three/drei';
import * as THREE from 'three';
import {
    fetchCurrentMonthSatellites,
    getSatellitePosition,
    getOrbitPath,
    getOrbitType
} from '../services/satelliteService';

// NASA texture URLs (public domain) - using reliable CDN sources
const EARTH_TEXTURE = 'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/planets/earth_atmos_2048.jpg';
const EARTH_BUMP = 'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/planets/earth_normal_2048.jpg';
const MOON_TEXTURE = 'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/planets/moon_1024.jpg';

// Earth physical constants
const EARTH_AXIAL_TILT = 23.44 * (Math.PI / 180); // 23.44 degrees in radians
const EARTH_ROTATION_PERIOD = 86164.1; // Sidereal day in seconds (23h 56m 4.1s)
// Real-time rotation speed - Earth completes one rotation in ~24 hours
// This keeps satellite positions accurate relative to Earth's surface
const EARTH_ROTATION_SPEED = (2 * Math.PI) / EARTH_ROTATION_PERIOD; // Radians per second (real-time)
const CLOUD_ROTATION_SPEED = EARTH_ROTATION_SPEED * 1.02; // Clouds rotate very slightly faster

// Loading fallback - simple sphere while textures load
function EarthFallback() {
    const earthRef = useRef();

    useFrame(({ clock }) => {
        if (earthRef.current) {
            earthRef.current.rotation.y = clock.getElapsedTime() * 0.05;
        }
    });

    return (
        <Sphere ref={earthRef} args={[2, 32, 32]}>
            <meshStandardMaterial color="#2171b5" roughness={0.8} />
        </Sphere>
    );
}

// Earth component with NASA textures
// The Earth rotates inside the ECI (Earth-Centered Inertial) frame
// Satellites remain fixed in ECI while Earth rotates beneath them
function Earth() {
    const earthGroupRef = useRef(); // Group for axial tilt
    const earthRef = useRef();
    const cloudsRef = useRef();

    // Load NASA textures (earth and bump only - clouds are procedural)
    const [earthTexture, bumpTexture] = useLoader(THREE.TextureLoader, [
        EARTH_TEXTURE,
        EARTH_BUMP
    ]);

    // Calculate initial rotation based on current time (Greenwich Sidereal Time)
    const initialRotation = useMemo(() => {
        const now = new Date();
        // Calculate approximate GMST (Greenwich Mean Sidereal Time)
        // This aligns the Earth texture so Greenwich is at the correct position
        const jd = (now.getTime() / 86400000) + 2440587.5; // Julian date
        const t = (jd - 2451545.0) / 36525.0; // Julian centuries from J2000
        const gmst = 280.46061837 + 360.98564736629 * (jd - 2451545.0) +
            0.000387933 * t * t - (t * t * t) / 38710000;
        return (gmst % 360) * (Math.PI / 180); // Convert to radians
    }, []);

    useFrame((state, delta) => {
        if (earthRef.current) {
            // Rotate Earth at visual speed (sped up for effect)
            earthRef.current.rotation.y += EARTH_ROTATION_SPEED * delta;
        }
        if (cloudsRef.current) {
            // Clouds rotate slightly faster than Earth surface
            cloudsRef.current.rotation.y += CLOUD_ROTATION_SPEED * delta;
        }
    });

    // Set initial rotation on mount
    useEffect(() => {
        if (earthRef.current) {
            earthRef.current.rotation.y = initialRotation;
        }
        if (cloudsRef.current) {
            cloudsRef.current.rotation.y = initialRotation;
        }
    }, [initialRotation]);

    // Earth material with textures
    const earthMaterial = useMemo(() => {
        return new THREE.MeshStandardMaterial({
            map: earthTexture,
            normalMap: bumpTexture,
            normalScale: new THREE.Vector2(0.5, 0.5),
            roughness: 0.7,
            metalness: 0.1
        });
    }, [earthTexture, bumpTexture]);

    // Procedural cloud material
    const cloudMaterial = useMemo(() => {
        const canvas = document.createElement('canvas');
        canvas.width = 2048;
        canvas.height = 1024;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, 2048, 1024);

        // Draw cloud clusters
        const drawCloud = (x, y, size, opacity) => {
            for (let i = 0; i < 8; i++) {
                const ox = (Math.random() - 0.5) * size * 1.5;
                const oy = (Math.random() - 0.5) * size * 0.4;
                const r = Math.random() * size * 0.5 + size * 0.2;
                const g = ctx.createRadialGradient(x + ox, y + oy, 0, x + ox, y + oy, r);
                g.addColorStop(0, `rgba(255,255,255,${opacity})`);
                g.addColorStop(0.5, `rgba(255,255,255,${opacity * 0.5})`);
                g.addColorStop(1, 'rgba(255,255,255,0)');
                ctx.fillStyle = g;
                ctx.beginPath();
                ctx.arc(x + ox, y + oy, r, 0, Math.PI * 2);
                ctx.fill();
            }
        };

        for (let i = 0; i < 80; i++) {
            drawCloud(Math.random() * 2048, Math.random() * 1024, 30 + Math.random() * 80, 0.3 + Math.random() * 0.4);
        }

        const texture = new THREE.CanvasTexture(canvas);
        return new THREE.MeshStandardMaterial({
            map: texture,
            transparent: true,
            opacity: 0.6,
            depthWrite: false,
        });
    }, []);

    // Atmosphere material
    const atmosphereMaterial = useMemo(() => {
        return new THREE.ShaderMaterial({
            uniforms: {
                glowColor: { value: new THREE.Color(0x4da6ff) }
            },
            vertexShader: `
                varying vec3 vNormal;
                void main() {
                    vNormal = normalize(normalMatrix * normal);
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                varying vec3 vNormal;
                uniform vec3 glowColor;
                void main() {
                    float intensity = pow(0.7 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.0);
                    gl_FragColor = vec4(glowColor, intensity * 0.5);
                }
            `,
            transparent: true,
            side: THREE.BackSide,
            blending: THREE.AdditiveBlending
        });
    }, []);

    return (
        // Apply axial tilt to the entire Earth system
        // Tilt is around the X-axis to simulate Earth's 23.44° obliquity
        <group ref={earthGroupRef} rotation={[EARTH_AXIAL_TILT, 0, 0]}>
            {/* Atmosphere glow */}
            <Sphere args={[2.15, 64, 64]}>
                <primitive object={atmosphereMaterial} attach="material" />
            </Sphere>

            {/* Earth - rotates around Y axis (polar axis after tilt) */}
            <Sphere ref={earthRef} args={[2, 64, 64]}>
                <primitive object={earthMaterial} attach="material" />
            </Sphere>

            {/* Clouds - rotate slightly faster than Earth */}
            <Sphere ref={cloudsRef} args={[2.02, 64, 64]}>
                <primitive object={cloudMaterial} attach="material" />
            </Sphere>
        </group>
    );
}

// Moon component with real texture orbiting Earth
function Moon() {
    const moonGroupRef = useRef();
    const moonRef = useRef();

    // Moon orbital parameters (scaled for visual effect)
    const MOON_ORBIT_RADIUS = 4.5; // Distance from Earth center (compact to fit in scene)
    const MOON_SIZE = 0.45; // Moon is about 27% of Earth's diameter
    const MOON_ORBIT_SPEED = 0.08; // Orbit speed (faster for visual effect)
    const MOON_ORBIT_INCLINATION = 5.14 * (Math.PI / 180); // Moon's orbital inclination ~5.14°

    // Load moon texture
    const moonTexture = useLoader(THREE.TextureLoader, MOON_TEXTURE);

    // Moon is tidally locked - same face always points to Earth
    useFrame((state, delta) => {
        if (moonGroupRef.current) {
            // Orbit around Earth
            moonGroupRef.current.rotation.y += MOON_ORBIT_SPEED * delta;
        }
        if (moonRef.current) {
            // Moon rotates to keep same face toward Earth (tidal locking)
            // Since it's a child of the orbiting group, we just need to counter-rotate
            moonRef.current.rotation.y += MOON_ORBIT_SPEED * delta;
        }
    });

    const moonMaterial = useMemo(() => {
        return new THREE.MeshStandardMaterial({
            map: moonTexture,
            roughness: 0.9,
            metalness: 0.0,
        });
    }, [moonTexture]);

    return (
        // Orbital plane with slight inclination
        <group rotation={[MOON_ORBIT_INCLINATION, 0, 0]}>
            {/* Rotating group for orbit */}
            <group ref={moonGroupRef}>
                {/* Moon positioned at orbit radius */}
                <Sphere
                    ref={moonRef}
                    args={[MOON_SIZE, 32, 32]}
                    position={[MOON_ORBIT_RADIUS, 0, 0]}
                >
                    <primitive object={moonMaterial} attach="material" />
                </Sphere>
            </group>
        </group>
    );
}

// Real Satellite component using TLE data
function RealSatellite({ satelliteData, size = 0.03, isSelected = false, onSelect }) {
    const satelliteRef = useRef();
    const [hovered, setHovered] = useState(false);

    useFrame(() => {
        if (satelliteRef.current && satelliteData.satrec) {
            const position = getSatellitePosition(satelliteData.satrec);
            if (position) {
                satelliteRef.current.position.x = position.x;
                satelliteRef.current.position.y = position.y;
                satelliteRef.current.position.z = position.z;
            }
        }
    });

    const orbitType = getOrbitType(satelliteData.satrec);
    const satColor = orbitType === 'LEO' ? '#00d4ff' : orbitType === 'MEO' ? '#ffaa00' : '#ff4444';
    const isHighlighted = hovered || isSelected;

    const handleClick = (e) => {
        e.stopPropagation();
        if (onSelect) {
            onSelect(satelliteData);
        }
    };

    return (
        <group
            ref={satelliteRef}
            onPointerOver={() => setHovered(true)}
            onPointerOut={() => setHovered(false)}
            onClick={handleClick}
        >
            {/* Satellite body */}
            <mesh>
                <boxGeometry args={[size * 2, size, size]} />
                <meshStandardMaterial
                    color={isHighlighted ? "#ffffff" : "#c0c0c0"}
                    metalness={0.8}
                    roughness={0.2}
                    emissive={isHighlighted ? satColor : "#000000"}
                    emissiveIntensity={isHighlighted ? 0.5 : 0}
                />
            </mesh>
            {/* Solar panels */}
            <mesh position={[-size * 2.5, 0, 0]}>
                <boxGeometry args={[size * 2, size * 0.1, size * 1.5]} />
                <meshStandardMaterial color="#1a4a7a" metalness={0.5} roughness={0.3} />
            </mesh>
            <mesh position={[size * 2.5, 0, 0]}>
                <boxGeometry args={[size * 2, size * 0.1, size * 1.5]} />
                <meshStandardMaterial color="#1a4a7a" metalness={0.5} roughness={0.3} />
            </mesh>
            {/* Satellite indicator dot */}
            <mesh position={[0, size * 2, 0]}>
                <sphereGeometry args={[size * 0.5, 8, 8]} />
                <meshBasicMaterial color={satColor} />
            </mesh>
            {/* Selection ring when selected */}
            {isSelected && (
                <mesh rotation={[Math.PI / 2, 0, 0]}>
                    <ringGeometry args={[size * 3, size * 3.5, 32]} />
                    <meshBasicMaterial color={satColor} transparent opacity={0.6} side={THREE.DoubleSide} />
                </mesh>
            )}
            {/* Name label on hover (brief) */}
            {hovered && !isSelected && (
                <Html position={[0, size * 4, 0]} center style={{ pointerEvents: 'none' }}>
                    <div style={{
                        background: 'rgba(0, 20, 40, 0.85)',
                        border: '1px solid #00d4ff',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        color: '#00d4ff',
                        fontSize: '10px',
                        fontFamily: 'monospace',
                        whiteSpace: 'nowrap'
                    }}>
                        {satelliteData.name}
                    </div>
                </Html>
            )}
        </group>
    );
}

// Orbit path visualization using TLE data
function RealOrbitPath({ satelliteData, color = "#00d4ff", opacity = 0.15 }) {
    const points = useMemo(() => {
        if (!satelliteData.satrec) return [];
        return getOrbitPath(satelliteData.satrec, 100);
    }, [satelliteData]);

    if (points.length < 2) return null;

    return (
        <Line
            points={points}
            color={color}
            lineWidth={0.5}
            transparent
            opacity={opacity}
        />
    );
}

// Fallback satellite for when TLE data isn't available
function FallbackSatellite({ orbitRadius, speed, startAngle, size = 0.05 }) {
    const satelliteRef = useRef();

    useFrame(({ clock }) => {
        const elapsed = clock.getElapsedTime();
        const angle = startAngle + elapsed * speed;

        if (satelliteRef.current) {
            satelliteRef.current.position.x = Math.cos(angle) * orbitRadius;
            satelliteRef.current.position.z = Math.sin(angle) * orbitRadius;
            satelliteRef.current.position.y = Math.sin(angle * 0.5) * 0.3;
        }
    });

    return (
        <group ref={satelliteRef}>
            <mesh>
                <boxGeometry args={[size * 2, size, size]} />
                <meshStandardMaterial color="#c0c0c0" metalness={0.8} roughness={0.2} />
            </mesh>
            <mesh position={[-size * 2.5, 0, 0]}>
                <boxGeometry args={[size * 2, size * 0.1, size * 1.5]} />
                <meshStandardMaterial color="#1a4a7a" metalness={0.5} roughness={0.3} />
            </mesh>
            <mesh position={[size * 2.5, 0, 0]}>
                <boxGeometry args={[size * 2, size * 0.1, size * 1.5]} />
                <meshStandardMaterial color="#1a4a7a" metalness={0.5} roughness={0.3} />
            </mesh>
        </group>
    );
}

// Rocket Second Stage - tumbling debris in orbit
function SecondStage({ orbitRadius, speed, startAngle, size = 0.08, tumbleSpeed = 1, inclination = 0 }) {
    const stageRef = useRef();

    useFrame(({ clock }) => {
        const elapsed = clock.getElapsedTime();
        const angle = startAngle + elapsed * speed;

        if (stageRef.current) {
            // Orbital position
            stageRef.current.position.x = Math.cos(angle) * orbitRadius;
            stageRef.current.position.z = Math.sin(angle) * orbitRadius;
            stageRef.current.position.y = Math.sin(angle + inclination) * 0.5;

            // Tumbling rotation (spent stages tumble in orbit)
            stageRef.current.rotation.x = elapsed * tumbleSpeed * 0.5;
            stageRef.current.rotation.y = elapsed * tumbleSpeed * 0.3;
            stageRef.current.rotation.z = elapsed * tumbleSpeed * 0.7;
        }
    });

    return (
        <group ref={stageRef}>
            {/* Main tank body - cylindrical */}
            <mesh>
                <cylinderGeometry args={[size * 0.4, size * 0.4, size * 3, 16]} />
                <meshStandardMaterial color="#e8e8e8" metalness={0.6} roughness={0.4} />
            </mesh>

            {/* Tank stripe/livery */}
            <mesh position={[0, size * 0.5, 0]}>
                <cylinderGeometry args={[size * 0.42, size * 0.42, size * 0.4, 16]} />
                <meshStandardMaterial color="#1a1a1a" metalness={0.3} roughness={0.6} />
            </mesh>

            {/* Upper dome */}
            <mesh position={[0, size * 1.5, 0]}>
                <sphereGeometry args={[size * 0.4, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
                <meshStandardMaterial color="#e8e8e8" metalness={0.6} roughness={0.4} />
            </mesh>

            {/* Engine bell */}
            <mesh position={[0, -size * 1.6, 0]} rotation={[Math.PI, 0, 0]}>
                <coneGeometry args={[size * 0.5, size * 0.6, 16, 1, true]} />
                <meshStandardMaterial color="#4a4a4a" metalness={0.8} roughness={0.3} side={THREE.DoubleSide} />
            </mesh>

            {/* Engine inner glow/detail */}
            <mesh position={[0, -size * 1.4, 0]}>
                <circleGeometry args={[size * 0.35, 16]} />
                <meshStandardMaterial color="#2a2a2a" metalness={0.9} roughness={0.2} />
            </mesh>

            {/* Interstage ring */}
            <mesh position={[0, -size * 1.2, 0]}>
                <torusGeometry args={[size * 0.42, size * 0.05, 8, 16]} />
                <meshStandardMaterial color="#3a3a3a" metalness={0.7} roughness={0.3} />
            </mesh>
        </group>
    );
}

// International Space Station (ISS)
function ISS({ orbitRadius = 2.35, speed = 0.4, startAngle = 0 }) {
    const issRef = useRef();

    // ISS orbits at 51.6° inclination
    const ISS_INCLINATION = 51.6 * (Math.PI / 180);

    useFrame(({ clock }) => {
        const elapsed = clock.getElapsedTime();
        const angle = startAngle + elapsed * speed;

        if (issRef.current) {
            // Calculate orbital position with inclination
            const x = Math.cos(angle) * orbitRadius;
            const z = Math.sin(angle) * orbitRadius;
            const y = Math.sin(angle) * Math.sin(ISS_INCLINATION) * orbitRadius * 0.4;

            issRef.current.position.set(x, y, z);

            // ISS always faces direction of travel
            issRef.current.rotation.y = -angle + Math.PI / 2;
            // Slight pitch to follow orbit curve
            issRef.current.rotation.x = Math.sin(angle) * 0.1;
        }
    });

    const solarPanelMaterial = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#1a237e',
        metalness: 0.8,
        roughness: 0.3,
        side: THREE.DoubleSide
    }), []);

    const moduleMaterial = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#e0e0e0',
        metalness: 0.4,
        roughness: 0.6
    }), []);

    const goldFoilMaterial = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#ffd700',
        metalness: 0.9,
        roughness: 0.2
    }), []);

    const size = 0.12; // Scale factor

    return (
        <group ref={issRef}>
            {/* Main Truss (horizontal backbone) */}
            <mesh>
                <boxGeometry args={[size * 8, size * 0.15, size * 0.15]} />
                <primitive object={moduleMaterial} attach="material" />
            </mesh>

            {/* Pressurized Modules (center) */}
            {/* US Destiny Lab */}
            <mesh position={[0, 0, size * 0.3]}>
                <cylinderGeometry args={[size * 0.2, size * 0.2, size * 1.2, 16]} rotation={[Math.PI / 2, 0, 0]} />
                <primitive object={moduleMaterial} attach="material" />
            </mesh>

            {/* Node modules */}
            <mesh position={[size * 0.7, 0, size * 0.3]}>
                <cylinderGeometry args={[size * 0.18, size * 0.18, size * 0.5, 16]} rotation={[Math.PI / 2, 0, 0]} />
                <primitive object={moduleMaterial} attach="material" />
            </mesh>

            <mesh position={[-size * 0.7, 0, size * 0.3]}>
                <cylinderGeometry args={[size * 0.18, size * 0.18, size * 0.5, 16]} rotation={[Math.PI / 2, 0, 0]} />
                <primitive object={moduleMaterial} attach="material" />
            </mesh>

            {/* Russian Segment */}
            <mesh position={[-size * 1.2, 0, size * 0.4]}>
                <cylinderGeometry args={[size * 0.22, size * 0.18, size * 0.8, 16]} rotation={[Math.PI / 2, 0, 0]} />
                <primitive object={goldFoilMaterial} attach="material" />
            </mesh>

            {/* Solar Array Wings - Port Side (4 panels) */}
            {[-3, -1.5].map((xPos, i) => (
                <group key={`port-${i}`} position={[size * xPos, 0, 0]}>
                    {/* Upper panel */}
                    <mesh position={[0, size * 1.2, 0]}>
                        <boxGeometry args={[size * 0.8, size * 2, size * 0.02]} />
                        <primitive object={solarPanelMaterial} attach="material" />
                    </mesh>
                    {/* Lower panel */}
                    <mesh position={[0, -size * 1.2, 0]}>
                        <boxGeometry args={[size * 0.8, size * 2, size * 0.02]} />
                        <primitive object={solarPanelMaterial} attach="material" />
                    </mesh>
                    {/* Panel support mast */}
                    <mesh>
                        <boxGeometry args={[size * 0.05, size * 0.4, size * 0.05]} />
                        <primitive object={moduleMaterial} attach="material" />
                    </mesh>
                </group>
            ))}

            {/* Solar Array Wings - Starboard Side (4 panels) */}
            {[1.5, 3].map((xPos, i) => (
                <group key={`starboard-${i}`} position={[size * xPos, 0, 0]}>
                    {/* Upper panel */}
                    <mesh position={[0, size * 1.2, 0]}>
                        <boxGeometry args={[size * 0.8, size * 2, size * 0.02]} />
                        <primitive object={solarPanelMaterial} attach="material" />
                    </mesh>
                    {/* Lower panel */}
                    <mesh position={[0, -size * 1.2, 0]}>
                        <boxGeometry args={[size * 0.8, size * 2, size * 0.02]} />
                        <primitive object={solarPanelMaterial} attach="material" />
                    </mesh>
                    {/* Panel support mast */}
                    <mesh>
                        <boxGeometry args={[size * 0.05, size * 0.4, size * 0.05]} />
                        <primitive object={moduleMaterial} attach="material" />
                    </mesh>
                </group>
            ))}

            {/* Radiator panels (white, perpendicular to solar panels) */}
            <mesh position={[size * 2.2, 0, size * 0.6]} rotation={[Math.PI / 2, 0, 0]}>
                <boxGeometry args={[size * 0.6, size * 1.5, size * 0.02]} />
                <meshStandardMaterial color="#ffffff" metalness={0.3} roughness={0.7} side={THREE.DoubleSide} />
            </mesh>
            <mesh position={[-size * 2.2, 0, size * 0.6]} rotation={[Math.PI / 2, 0, 0]}>
                <boxGeometry args={[size * 0.6, size * 1.5, size * 0.02]} />
                <meshStandardMaterial color="#ffffff" metalness={0.3} roughness={0.7} side={THREE.DoubleSide} />
            </mesh>
        </group>
    );
}

// Blue Moon MK2 Lunar Lander (Blue Origin design for Artemis program)
function LunarLander({ orbitRadius = 2.6, speed = 0.25, startAngle = 0 }) {
    const landerRef = useRef();

    // Higher orbit for trans-lunar injection staging
    const ORBIT_INCLINATION = 28.5 * (Math.PI / 180); // Kennedy Space Center latitude

    useFrame(({ clock }) => {
        const elapsed = clock.getElapsedTime();
        const angle = startAngle + elapsed * speed;

        if (landerRef.current) {
            const x = Math.cos(angle) * orbitRadius;
            const z = Math.sin(angle) * orbitRadius;
            const y = Math.sin(angle) * Math.sin(ORBIT_INCLINATION) * orbitRadius * 0.3;

            landerRef.current.position.set(x, y, z);

            // Lander orientation - engines pointing down
            landerRef.current.rotation.y = -angle;
            landerRef.current.rotation.z = Math.sin(elapsed * 0.3) * 0.02; // Very slight wobble
        }
    });

    const size = 0.1; // Slightly larger for Blue Moon MK2

    // Blue Origin blue color
    const blueOriginBlue = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#0033a0',
        metalness: 0.6,
        roughness: 0.4
    }), []);

    const hullMaterial = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#e8e8e8',
        metalness: 0.5,
        roughness: 0.4
    }), []);

    const goldMaterial = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#d4af37',
        metalness: 0.85,
        roughness: 0.15
    }), []);

    const darkMaterial = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#1a1a1a',
        metalness: 0.6,
        roughness: 0.4
    }), []);

    const engineMaterial = useMemo(() => new THREE.MeshStandardMaterial({
        color: '#2d2d2d',
        metalness: 0.9,
        roughness: 0.2
    }), []);

    return (
        <group ref={landerRef}>
            {/* === DESCENT STAGE (Large propellant tanks with gold MLI) === */}

            {/* Main descent stage body - tall cylinder with gold MLI insulation */}
            <mesh position={[0, 0, 0]}>
                <cylinderGeometry args={[size * 1.0, size * 1.2, size * 2.5, 8]} />
                <primitive object={goldMaterial} attach="material" />
            </mesh>

            {/* Blue Origin stripe/band around descent stage */}
            <mesh position={[0, size * 0.8, 0]}>
                <cylinderGeometry args={[size * 1.02, size * 1.02, size * 0.3, 8]} />
                <primitive object={blueOriginBlue} attach="material" />
            </mesh>

            {/* Lower tank section (slightly wider base) */}
            <mesh position={[0, -size * 1.0, 0]}>
                <cylinderGeometry args={[size * 1.2, size * 1.4, size * 0.8, 8]} />
                <primitive object={goldMaterial} attach="material" />
            </mesh>

            {/* === CREW MODULE (Ascent Stage) === */}

            {/* Crew cabin - cylindrical with white finish */}
            <mesh position={[0, size * 1.8, 0]}>
                <cylinderGeometry args={[size * 0.75, size * 0.9, size * 1.0, 16]} />
                <primitive object={hullMaterial} attach="material" />
            </mesh>

            {/* Crew cabin dome/roof */}
            <mesh position={[0, size * 2.4, 0]}>
                <sphereGeometry args={[size * 0.7, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
                <primitive object={hullMaterial} attach="material" />
            </mesh>

            {/* Docking port on top */}
            <mesh position={[0, size * 2.9, 0]}>
                <cylinderGeometry args={[size * 0.25, size * 0.25, size * 0.2, 16]} />
                <primitive object={darkMaterial} attach="material" />
            </mesh>

            {/* Windows (triangular arrangement like Blue Moon renders) */}
            {[0, Math.PI * 0.66, Math.PI * 1.33].map((angle, i) => (
                <mesh
                    key={`window-${i}`}
                    position={[
                        Math.sin(angle) * size * 0.78,
                        size * 1.9,
                        Math.cos(angle) * size * 0.78
                    ]}
                    rotation={[0, angle, 0]}
                >
                    <boxGeometry args={[size * 0.3, size * 0.25, size * 0.05]} />
                    <meshStandardMaterial color="#0a0a0a" metalness={0.95} roughness={0.05} />
                </mesh>
            ))}

            {/* === LANDING LEGS (4 legs, Blue Moon style) === */}
            {[0, Math.PI / 2, Math.PI, Math.PI * 1.5].map((angle, i) => (
                <group key={`leg-${i}`} rotation={[0, angle, 0]}>
                    {/* Primary strut (angled outward) */}
                    <mesh position={[size * 1.1, -size * 1.0, 0]} rotation={[0, 0, 0.5]}>
                        <cylinderGeometry args={[size * 0.05, size * 0.05, size * 1.4, 8]} />
                        <primitive object={darkMaterial} attach="material" />
                    </mesh>
                    {/* Secondary strut (support) */}
                    <mesh position={[size * 0.9, -size * 0.5, 0]} rotation={[0, 0, 0.8]}>
                        <cylinderGeometry args={[size * 0.03, size * 0.03, size * 0.8, 8]} />
                        <primitive object={darkMaterial} attach="material" />
                    </mesh>
                    {/* Foot pad (large circular) */}
                    <mesh position={[size * 1.6, -size * 1.7, 0]} rotation={[0, 0, 0]}>
                        <cylinderGeometry args={[size * 0.35, size * 0.4, size * 0.06, 12]} />
                        <primitive object={darkMaterial} attach="material" />
                    </mesh>
                </group>
            ))}

            {/* === BE-7 ENGINES (3 engines in triangle pattern) === */}
            {[0, Math.PI * 0.66, Math.PI * 1.33].map((angle, i) => (
                <group key={`engine-${i}`} position={[
                    Math.sin(angle) * size * 0.5,
                    -size * 1.5,
                    Math.cos(angle) * size * 0.5
                ]}>
                    {/* Engine bell */}
                    <mesh rotation={[Math.PI, 0, 0]}>
                        <coneGeometry args={[size * 0.35, size * 0.5, 16, 1, true]} />
                        <primitive object={engineMaterial} attach="material" />
                    </mesh>
                    {/* Engine inner glow */}
                    <mesh position={[0, -size * 0.1, 0]} rotation={[Math.PI / 2, 0, 0]}>
                        <circleGeometry args={[size * 0.25, 16]} />
                        <meshStandardMaterial color="#1a1a1a" metalness={0.9} roughness={0.1} />
                    </mesh>
                </group>
            ))}

            {/* === RCS THRUSTERS (around crew module) === */}
            {[0, Math.PI / 2, Math.PI, Math.PI * 1.5].map((angle, i) => (
                <mesh
                    key={`rcs-${i}`}
                    position={[
                        Math.sin(angle) * size * 0.95,
                        size * 1.5,
                        Math.cos(angle) * size * 0.95
                    ]}
                >
                    <boxGeometry args={[size * 0.12, size * 0.2, size * 0.12]} />
                    <primitive object={hullMaterial} attach="material" />
                </mesh>
            ))}

            {/* === HIGH GAIN ANTENNA === */}
            <group position={[size * 0.6, size * 2.6, size * 0.3]} rotation={[0.4, 0.3, 0]}>
                {/* Dish */}
                <mesh>
                    <sphereGeometry args={[size * 0.2, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
                    <primitive object={hullMaterial} attach="material" />
                </mesh>
                {/* Feed horn */}
                <mesh position={[0, size * 0.15, 0]}>
                    <cylinderGeometry args={[size * 0.02, size * 0.02, size * 0.15, 8]} />
                    <primitive object={darkMaterial} attach="material" />
                </mesh>
            </group>
        </group>
    );
}

// Simple orbit path visualization (for fallback)
function SimpleOrbitPath({ radius }) {
    const points = useMemo(() => {
        const pts = [];
        for (let i = 0; i <= 64; i++) {
            const angle = (i / 64) * Math.PI * 2;
            pts.push([Math.cos(angle) * radius, 0, Math.sin(angle) * radius]);
        }
        return pts;
    }, [radius]);

    return (
        <Line
            points={points}
            color="#ffffff"
            lineWidth={0.5}
            transparent
            opacity={0.1}
        />
    );
}

// Satellite constellation display component
function SatelliteConstellation({ satellites, maxDisplay = 25, selectedSatellite, onSatelliteSelect }) {
    // Limit display to prevent performance issues
    const displaySatellites = satellites.slice(0, maxDisplay);

    return (
        <group>
            {displaySatellites.map((sat, index) => (
                <React.Fragment key={sat.noradId || index}>
                    <RealSatellite
                        satelliteData={sat}
                        size={0.025}
                        isSelected={selectedSatellite?.noradId === sat.noradId}
                        onSelect={onSatelliteSelect}
                    />
                    <RealOrbitPath
                        satelliteData={sat}
                        opacity={selectedSatellite?.noradId === sat.noradId ? 0.3 : 0.08}
                    />
                </React.Fragment>
            ))}
        </group>
    );
}



// Slowly rotating group that contains Earth and all satellites
function RotatingScene({ children }) {
    const groupRef = useRef();

    useFrame((state, delta) => {
        if (groupRef.current) {
            // Slow rotation - one full rotation every 60 seconds
            groupRef.current.rotation.y += delta * 0.1;
        }
    });

    return (
        <group ref={groupRef}>
            {children}
        </group>
    );
}

// Scene content component (needs to be inside Canvas)
function SceneContent({ satellites, loading, selectedSatellite, onSatelliteSelect }) {
    return (
        <>
            {/* Lighting - outside rotating group so it stays fixed */}
            <ambientLight intensity={0.15} />
            <directionalLight position={[5, 3, 5]} intensity={1.5} color="#ffffff" />
            <pointLight position={[-10, -10, -10]} intensity={0.3} color="#4a90d9" />

            {/* Everything inside rotates together slowly */}
            <RotatingScene>
                {/* Earth and atmosphere */}
                <Suspense fallback={<EarthFallback />}>
                    <Earth />
                </Suspense>

                {/* Moon orbiting Earth */}
                <Suspense fallback={null}>
                    <Moon />
                </Suspense>

                {/* Real satellites from TLE data */}
                {!loading && satellites.length > 0 ? (
                    <SatelliteConstellation
                        satellites={satellites}
                        maxDisplay={30}
                        selectedSatellite={selectedSatellite}
                        onSatelliteSelect={onSatelliteSelect}
                    />
                ) : (
                    /* Fallback satellites while loading */
                    <>
                        <SimpleOrbitPath radius={2.4} />
                        <SimpleOrbitPath radius={2.55} />
                        <SimpleOrbitPath radius={2.7} />
                        <FallbackSatellite orbitRadius={2.4} speed={0.5} startAngle={0} size={0.04} />
                        <FallbackSatellite orbitRadius={2.55} speed={0.4} startAngle={2.1} size={0.035} />
                        <FallbackSatellite orbitRadius={2.7} speed={0.32} startAngle={4.2} size={0.03} />
                    </>
                )}

                {/* Spent second stages - tumbling in orbit */}
                <SecondStage
                    orbitRadius={2.9}
                    speed={0.15}
                    startAngle={1}
                    size={0.06}
                    tumbleSpeed={0.8}
                    inclination={0.5}
                />
                <SecondStage
                    orbitRadius={3.2}
                    speed={0.12}
                    startAngle={3.5}
                    size={0.05}
                    tumbleSpeed={1.2}
                    inclination={-0.3}
                />

                {/* International Space Station */}
                <ISS orbitRadius={2.4} speed={0.35} startAngle={2.5} />

                {/* Lunar Lander in parking orbit */}
                <LunarLander orbitRadius={2.7} speed={0.2} startAngle={4.0} />
            </RotatingScene>

            {/* Controls - allow user to interact */}
            <OrbitControls
                enableZoom={false}
                enablePan={false}
                rotateSpeed={0.3}
                minPolarAngle={Math.PI / 3}
                maxPolarAngle={Math.PI / 1.5}
            />
        </>
    );
}

// Main Earth3D component
function Earth3D({ onSatellitesLoaded }) {
    const [satellites, setSatellites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSatellite, setSelectedSatellite] = useState(null);

    useEffect(() => {
        async function loadSatellites() {
            try {
                setLoading(true);
                const sats = await fetchCurrentMonthSatellites();
                setSatellites(sats);
                if (onSatellitesLoaded) {
                    onSatellitesLoaded({ count: sats.length, loading: false });
                }
            } catch (error) {
                console.error('Failed to load satellites:', error);
                if (onSatellitesLoaded) {
                    onSatellitesLoaded({ count: 0, loading: false });
                }
            } finally {
                setLoading(false);
            }
        }

        loadSatellites();
    }, [onSatellitesLoaded]);

    const handleSatelliteSelect = (satellite) => {
        // Toggle selection - click again to deselect
        if (selectedSatellite?.noradId === satellite.noradId) {
            setSelectedSatellite(null);
        } else {
            setSelectedSatellite(satellite);
        }
    };

    const orbitType = selectedSatellite?.satrec ? getOrbitType(selectedSatellite.satrec) : '';

    return (
        <div className="earth-3d-container">
            <Canvas
                camera={{ position: [-5, 0, 8], fov: 50 }}
                style={{ background: 'transparent' }}
                gl={{ alpha: true, antialias: true }}
            >
                <SceneContent
                    satellites={satellites}
                    loading={loading}
                    selectedSatellite={selectedSatellite}
                    onSatelliteSelect={handleSatelliteSelect}
                />
            </Canvas>

            {/* Selected Satellite Info Panel */}
            {selectedSatellite && (
                <div className="satellite-info-panel">
                    <button
                        className="satellite-info-close"
                        onClick={() => setSelectedSatellite(null)}
                        aria-label="Close satellite info"
                    >
                        ×
                    </button>
                    <div className="satellite-info-header">
                        <span className="satellite-info-icon">🛰</span>
                        <span className="satellite-info-name">{selectedSatellite.name}</span>
                    </div>
                    <div className="satellite-info-grid">
                        <div className="satellite-info-item">
                            <span className="satellite-info-label">NORAD ID</span>
                            <span className="satellite-info-value">{selectedSatellite.noradId}</span>
                        </div>
                        <div className="satellite-info-item">
                            <span className="satellite-info-label">Orbit Type</span>
                            <span className="satellite-info-value orbit-type" data-type={orbitType}>{orbitType}</span>
                        </div>
                        <div className="satellite-info-item">
                            <span className="satellite-info-label">Inclination</span>
                            <span className="satellite-info-value">{selectedSatellite.inclination?.toFixed(2)}°</span>
                        </div>
                        <div className="satellite-info-item">
                            <span className="satellite-info-label">Period</span>
                            <span className="satellite-info-value">{selectedSatellite.period?.toFixed(1)} min</span>
                        </div>
                        <div className="satellite-info-item">
                            <span className="satellite-info-label">Eccentricity</span>
                            <span className="satellite-info-value">{selectedSatellite.eccentricity?.toFixed(4)}</span>
                        </div>
                        <div className="satellite-info-item">
                            <span className="satellite-info-label">Launched</span>
                            <span className="satellite-info-value">
                                {selectedSatellite.launchDate ? new Date(selectedSatellite.launchDate).toLocaleDateString() : 'Unknown'}
                            </span>
                        </div>
                    </div>
                    <div className="satellite-info-hint">
                        Click satellite again to deselect
                    </div>
                </div>
            )}
        </div>
    );
}

export default Earth3D;
